import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Ticket, Search, Loader2, Receipt, CheckCircle2, RefreshCw, Phone, Trophy } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import ContractorNav from '@/components/contractor/ContractorNav';
import PurchasedLeadCard from '@/components/contractor/PurchasedLeadCard';
import { PendingPaidBanner } from '@/components/contractor/PurchasedLeadsPreview';
import AwaitingPaymentNotice from '@/components/contractor/AwaitingPaymentNotice';
import { useMyLeads } from '@/hooks/useMyLeads';
import { fetchPurchaseHistory, HistoryRow, LEAD_STAGES, formatLeadDate } from '@/lib/myLeads';
import { canBuyLeads } from '@/lib/roles';
import { useLeadBalance } from '@/lib/creditsBus';
import { money, tradeLabel, LEAD_PRICE } from '@/data/constants';
import { parseSearch, matchesSearch } from '@/lib/naturalSearch';

const MyLeads: React.FC = () => {
  const { t, path, lang } = useI18n();
  const es = lang === 'es';
  const { profile, loading: authLoading } = useAuth();
  const { leads, pending, loading, error, justUnlocked, reload, setStage, unlockPending } = useMyLeads();
  const { balance } = useLeadBalance(profile?.id);
  const [q, setQ] = useState('');
  const [stage, setStageFilter] = useState<string>('all');
  const [history, setHistory] = useState<HistoryRow[]>([]);

  useEffect(() => {
    if (!profile?.id) return;
    fetchPurchaseHistory(profile.id).then(setHistory).catch(() => setHistory([]));
  }, [profile?.id, leads.length]);

  // NATURAL SEARCH (shared with the marketplace): "California", "CA", "Texas",
  // "Fontana", "roofing", "electrician", plus the buyer's OWN unlocked contact
  // name / phone / email, which are already on these cards.
  const parsedQ = useMemo(() => parseSearch(q), [q]);
  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (stage !== 'all' && l.stage !== stage) return false;
      if (parsedQ.empty) return true;
      const digits = (l.contact?.phone || '').replace(/\D/g, '');
      return matchesSearch(
        {
          title: l.listing?.title,
          description: l.listing?.description,
          city: l.listing?.city,
          state: l.listing?.state,
          trade: l.listing?.trade,
          project_type: l.listing?.project_type,
        },
        parsedQ,
        [l.contact?.contact_name, l.contact?.phone, digits, l.contact?.email, l.contact?.address],
      );
    });
  }, [leads, parsedQ, stage]);

  if (authLoading) return <div className="py-24 text-center text-slate-500">{t('common.loading')}</div>;
  if (!profile) return <Navigate to={`${path('/login')}?next=${encodeURIComponent(path('/my-leads'))}`} replace />;
  if (!canBuyLeads(profile.user_type)) return <Navigate to={path('/dashboard')} replace />;

  const counts = {
    total: leads.length,
    contacted: leads.filter((l) => ['contacted', 'estimate'].includes(l.stage)).length,
    won: leads.filter((l) => l.stage === 'won').length,
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-8 lg:px-8">
      <Seo title={es ? 'Mis Leads Comprados' : 'My Purchased Leads'} description="My purchased leads" noindex />
      <ContractorNav leadCount={loading ? undefined : leads.length} />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            <Ticket className="h-6 w-6 shrink-0 text-orange-500 sm:h-7 sm:w-7" /> {es ? 'Mis Leads Comprados' : 'My Purchased Leads'}
          </h1>
          <p className="mt-1 text-sm text-slate-500 sm:text-base">
            {es
              ? 'Todos los leads que ha pagado, con datos de contacto. Nunca pierde el acceso.'
              : 'Every lead you have paid for, with contact details. You never lose access.'}
          </p>
        </div>
        <button
          onClick={reload}
          aria-label={es ? 'Actualizar' : 'Refresh'}
          className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" /> <span className="hidden sm:inline">{es ? 'Actualizar' : 'Refresh'}</span>
        </button>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-2 sm:gap-3">
        {[
          { label: es ? 'Comprados' : 'Purchased', v: counts.total, icon: Ticket },
          { label: es ? 'En progreso' : 'In progress', v: counts.contacted, icon: Phone },
          { label: es ? 'Ganados' : 'Won', v: counts.won, icon: Trophy },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
            <s.icon className="h-5 w-5 text-orange-500" />
            <p className="mt-1.5 text-2xl font-extrabold tabular-nums text-slate-900">{loading ? '—' : s.v}</p>
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 sm:text-xs">{s.label}</p>
          </div>
        ))}
      </div>

      {justUnlocked > 0 && (
        <p className="mt-5 flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm font-bold text-emerald-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {es
            ? justUnlocked === 1
              ? 'Terminamos de desbloquear un lead que ya había pagado.'
              : `Terminamos de desbloquear ${justUnlocked} leads que ya había pagado.`
            : justUnlocked === 1
            ? 'We finished unlocking a lead you had already paid for.'
            : `We finished unlocking ${justUnlocked} leads you had already paid for.`}
        </p>
      )}

      <div className="mt-6">
        <AwaitingPaymentNotice ownedListingIds={leads.map((l) => l.listingId)} />
        <PendingPaidBanner pending={pending} onUnlock={unlockPending} />
      </div>

      {/* Search + filter only once there is something to search — for a
          brand-new buyer they were two empty controls above "no leads yet". */}
      {(loading || leads.length > 0) && (
      <div className="mt-2 flex flex-col gap-3 sm:flex-row">
        <label className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={es ? 'Buscar por proyecto, ciudad, nombre o teléfono' : 'Search by project, city, name or phone'}
            className="min-h-[44px] w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm"
          />
        </label>
        <select
          value={stage}
          onChange={(e) => setStageFilter(e.target.value)}
          aria-label={es ? 'Filtrar por estado' : 'Filter by status'}
          className="min-h-[44px] rounded-xl border border-slate-300 bg-white px-3 text-sm font-semibold"
        >
          <option value="all">{es ? 'Todos los estados' : 'All statuses'}</option>
          {LEAD_STAGES.map((s) => (
            <option key={s} value={s}>
              {t(`dash.stage.${s}`)}
            </option>
          ))}
        </select>
      </div>
      )}

      <div className="mt-6">
        {loading ? (
          <p className="flex items-center gap-2 py-12 text-sm font-semibold text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> {es ? 'Cargando sus leads…' : 'Loading your leads…'}
          </p>
        ) : error ? (
          <p className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
            {es ? 'No pudimos cargar sus leads. Toque Actualizar.' : 'We could not load your leads. Tap Refresh.'}
          </p>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center sm:p-10">
            <p className="text-base font-bold text-slate-800">
              {leads.length
                ? es
                  ? 'Ningún lead coincide con su búsqueda.'
                  : 'No leads match your search.'
                : es
                ? 'Aún no ha comprado leads.'
                : "You haven't purchased any leads yet."}
            </p>
            {/* First-time buyers: say plainly what happens next, one button. */}
            {!leads.length && (
              <>
                <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
                  {/* Members with credits unlock with one tap — telling them
                      "$29 per lead" made them think they would be charged. */}
                  {balance.spendable > 0
                    ? es
                      ? `Tiene ${balance.spendable} crédito${balance.spendable === 1 ? '' : 's'}. Elija un proyecto y toque "${t('jobs.unlockWithCredit')}". Aparece aquí al instante con el nombre y teléfono del cliente.`
                      : `You have ${balance.spendable} credit${balance.spendable === 1 ? '' : 's'}. Pick a project and tap "${t('jobs.unlockWithCredit')}". It shows up here instantly with the customer's name and phone.`
                    : es
                    ? `Elija un proyecto y toque Comprar. ${money(LEAD_PRICE)} por lead, sin membresía. Aparece aquí al instante con el nombre y teléfono del cliente.`
                    : `Pick a project and tap Buy. ${money(LEAD_PRICE)} per lead, no membership needed. It shows up here instantly with the customer's name and phone.`}
                </p>
                <Link
                  to={path('/jobs')}
                  className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 text-base font-bold text-white hover:bg-orange-600 sm:w-auto"
                >
                  <Search className="h-4 w-4" /> {es ? 'Buscar leads' : 'Find leads'}
                </Link>
              </>
            )}
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((l) => (
              <PurchasedLeadCard key={l.id} lead={l} onStage={setStage} />
            ))}
          </div>
        )}
      </div>

      {/* PURCHASE HISTORY — one list sorted by date, fully translated, stacks on phones.
          The whole section (heading included) is hidden until there is at
          least one row: a brand-new buyer used to see an orphan
          "Purchase history" heading with nothing under it. */}
      {(() => {
        const KIND: Record<string, [string, string]> = {
          single_lead: ['Single lead', 'Lead individual'],
          single: ['Single lead', 'Lead individual'],
          membership: ['Membership payment', 'Pago de membresía'],
          subscription: ['Membership payment', 'Pago de membresía'],
          credit_pack: ['Lead credits', 'Créditos de leads'],
          refund: ['Refund', 'Reembolso'],
        };
        const STATUS: Record<string, [string, string, string]> = {
          paid: ['Paid', 'Pagado', 'text-emerald-700'],
          succeeded: ['Paid', 'Pagado', 'text-emerald-700'],
          credited: ['Paid', 'Pagado', 'text-emerald-700'],
          completed: ['Paid', 'Pagado', 'text-emerald-700'],
          pending: ['Pending', 'Pendiente', 'text-amber-700'],
          failed: ['Failed', 'Fallido', 'text-red-700'],
          refunded: ['Refunded', 'Reembolsado', 'text-slate-500'],
          unlocked: ['Unlocked', 'Desbloqueado', 'text-emerald-700'],
        };
        const rows = [
          ...history.map((h) => ({
            id: h.id,
            at: h.createdAt,
            label: KIND[h.kind] ? (es ? KIND[h.kind][1] : KIND[h.kind][0]) : h.description || h.kind,
            status: h.status,
            amount: money(h.amount),
          })),
          ...leads.map((l) => ({
            id: `u-${l.id}`,
            at: l.createdAt,
            label: `${es ? 'Lead desbloqueado' : 'Lead unlocked'}: ${l.listing?.title || '—'}`,
            status: 'unlocked',
            amount: l.method === 'single_29' ? money(l.amount) : es ? '1 crédito' : '1 credit',
          })),
        ].sort((a, b) => (a.at < b.at ? 1 : -1));
        if (!rows.length) return null;
        return (
          <section className="mt-12">
            <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
              <Receipt className="h-5 w-5 text-orange-500" /> {es ? 'Historial de compras' : 'Purchase history'}
            </h2>
            <ul className="mt-4 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
              {rows.map((r) => {
                const s = STATUS[r.status] || [r.status, r.status, 'text-slate-600'];
                return (
                  <li key={r.id} className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
                    <div className="min-w-0">
                      <p className="break-words font-semibold text-slate-800">{r.label}</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {formatLeadDate(r.at, lang)} · <span className={`font-bold ${s[2]}`}>{es ? s[1] : s[0]}</span>
                      </p>
                    </div>
                    <span className="shrink-0 font-bold tabular-nums text-slate-900">{r.amount}</span>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })()}
    </div>
  );
};

export default MyLeads;
